import React from "react";
import _ from "lodash";
import { formatDateWithName } from "services/MomentUtils";
import { isCollectionNullOrEmpty } from "services/UtilityService";
import {
  ALL_FAX_TAG,
  UFL_URL_FILTER_CONFIG,
  UNPROCESSED_FAX_PROGRAM_TYPE,
  UNKNOWN,
} from "lookup/UnprocessedFaxListConstantsV2";
import {
  parseCsvParam,
  parseBooleanParam,
  QUERY_FILTER_TYPES,
  renderDateCell,
} from "commons/utils";

export const renderLastModifiedDateCol = (date, name) => 
  date
    ? renderDateCell(formatDateWithName(date, name))
    : React.createElement("span", null);

export const getFaxProgramType = (isSwitchProgram) => {
  if (isSwitchProgram) {
    return UNPROCESSED_FAX_PROGRAM_TYPE.SWITCH;
  } else {
    return UNPROCESSED_FAX_PROGRAM_TYPE.NON_SWITCH;
  }
};

export const getFaxTypeForRoute = (programType) => {
  switch (programType) {
    case UNPROCESSED_FAX_PROGRAM_TYPE.SWITCH:
      return "Switch";
    case UNPROCESSED_FAX_PROGRAM_TYPE.NON_SWITCH:
      return "NonSwitch";
    case UNPROCESSED_FAX_PROGRAM_TYPE.UNKNOWN:
    default:
      return "Unknown";
  }
};

const getCombinedTagsFromUrl = (urlFilters) => {
  const customerTags = Array.isArray(urlFilters.customerTags)
    ? urlFilters.customerTags
    : [];
  return Array.from(
    new Set([urlFilters.faxProgramCode, ...customerTags].filter(Boolean))
  );
};

export const getUrlFilterValues = (search) => {
  const params = new URLSearchParams(search || "");
  const values = {
    faxProgramCode: params.get("faxProgramCode"),
  };

  UFL_URL_FILTER_CONFIG.forEach(({ paramKey, valueType }) => {
    const paramValue = params.get(paramKey);
    if (
      valueType === QUERY_FILTER_TYPES.MULTI_SELECT ||
      valueType === QUERY_FILTER_TYPES.MULTI_SELECT_IDS
    ) {
      values[paramKey] = parseCsvParam(paramValue);
    } else if (valueType === QUERY_FILTER_TYPES.BOOLEAN) {
      values[paramKey] = parseBooleanParam(paramValue);
    } else {
      values[paramKey] = paramValue || "";
    }
  });

  return values;
};

export const getFilterValueFromUrl = (config, urlFilterValues) => {
  if (config.filterId === "tags") {
    return getCombinedTagsFromUrl(urlFilterValues);
  }

  const rawValue = urlFilterValues[config.paramKey];
  if (config.valueMap && typeof rawValue === "string" && rawValue) {
    return (
      config.valueMap[rawValue.toUpperCase()] || config.valueMap[rawValue] || ""
    );
  }
  return rawValue;
};

const hasFilterValue = (value, valueType) => {
  if (
    valueType === QUERY_FILTER_TYPES.MULTI_SELECT ||
    valueType === QUERY_FILTER_TYPES.MULTI_SELECT_IDS
  ) {
    return Array.isArray(value) && value.length > 0;
  }
  if (valueType === QUERY_FILTER_TYPES.BOOLEAN) {
    return value !== null;
  }
  return Boolean(value);
};

const upsertFilterValue = (filters, filterId, value) => {
  const filterIdx = filters.findIndex((f) => f.id === filterId);
  if (filterIdx > -1) {
    filters[filterIdx] = { ...filters[filterIdx], value, isdefault: true };
  } else {
    filters.push({ id: filterId, value, isdefault: true });
  }
};

export const normalizeMultiSelectIdsFilterValue = (value, options) => {
  if (!Array.isArray(value) || !Array.isArray(options) || options.length === 0)
    return value;
  if (value.length === 0) return value;
  const containsObjectValues = value.some(
    (entry) => entry && typeof entry === "object"
  );
  if (containsObjectValues) return value;

  return value
    .map((entry) => {
      const id = String(entry);
      return options.find((option) => String(option.id) === id) || { id, name: id };
    })
    .filter(Boolean);
};

export const buildPrimaryFilters = (
  currentFilters,
  urlFilterValues,
  faxProgramType
) => {
  let newFilters = _.cloneDeep(currentFilters);

  const associatedSwitchFilter = currentFilters.find(
    (f) => f.id === "associatedSwitchId"
  );
  const isAssociatedSwitchTrue =
    associatedSwitchFilter &&
    (associatedSwitchFilter.value === true ||
      associatedSwitchFilter.value === "true");
  const isSwitchFax = faxProgramType === UNPROCESSED_FAX_PROGRAM_TYPE.SWITCH;

  const shouldSkipTagsAndPriority = isSwitchFax && isAssociatedSwitchTrue;
  UFL_URL_FILTER_CONFIG.forEach((config) => {
    if (config.skipWhenSwitchAssociated && shouldSkipTagsAndPriority) return;
    const filterValue = getFilterValueFromUrl(config, urlFilterValues);
    if (!hasFilterValue(filterValue, config.valueType)) return;
    upsertFilterValue(newFilters, config.filterId, filterValue);
  });

  if (faxProgramType)
    newFilters.push({
      id: "faxProgramType",
      value: faxProgramType,
      isdefault: true,
    });

  return newFilters;
};

const tagEntryToId = (entry) =>
  typeof entry === "object" && entry !== null ? entry.id : entry;

const customerTagsPayloadFromTableTagIds = (tagIds, programCode) => {
  const ids = Array.isArray(tagIds) ? tagIds.map((id) => String(id)).filter(Boolean) : [];
  const withoutProgram = programCode
    ? ids.filter((id) => id !== String(programCode))
    : ids;
  if (withoutProgram.some((id) => id === ALL_FAX_TAG.id)) return ["ALL"];
  return Array.from(new Set(withoutProgram));
};

/** @param {string} [faxProgramCode] From URL — strips program id from tag ids for `customerTags`. */
export const filterModification = (filtered, faxProgramCode = "") => {
  let filteredDeepCopy = _.cloneDeep(filtered);

  if (filteredDeepCopy && filteredDeepCopy.length > 0) {
    const showMyRecentsFilter = filteredDeepCopy.find(
      (f) => f.id === "showMyRecentsOnly"
    );

    if (showMyRecentsFilter && showMyRecentsFilter.value === true) {
      const idx = filteredDeepCopy.findIndex((f) => f.id === "recentlyViewedUsers");
      if (idx > -1) filteredDeepCopy.splice(idx, 1);
    } else {
      const idx = filteredDeepCopy.findIndex((f) => f.id === "recentlyViewedUsers");
      if (idx > -1) {
        const filter = filteredDeepCopy[idx];
        if (filter.value.length > 0) {
          filteredDeepCopy[idx].value = filter.value
            .map((u) => (typeof u === "object" && u !== null ? u.id : u))
            .filter(Boolean);
        } else filteredDeepCopy.splice(idx, 1);
      }
    }

    const tagsIndex = filteredDeepCopy.findIndex((f) => f.id === "tags");
    const hadTagsFilter = tagsIndex > -1;
    let tagIds = [];
    if (hadTagsFilter) {
      const filter = filteredDeepCopy[tagsIndex];
      tagIds = (filter.value || []).map(tagEntryToId).filter(Boolean);
      filteredDeepCopy.splice(tagsIndex, 1);
    }

    // Treat the URL `faxProgramCode` (including `Unknown`) as the faxProgramTag.
    // This ensures we never accidentally send it as a customerTag.
    const programCode = faxProgramCode ? String(faxProgramCode) : "";

    const customerTagsPayload = hadTagsFilter
      ? customerTagsPayloadFromTableTagIds(tagIds, programCode)
      : [];

    if (hadTagsFilter) {
      if (programCode) {
        filteredDeepCopy.push({
          id: "faxProgramTag",
          value: programCode,
        });
      }
      filteredDeepCopy.push({
        id: "customerTags",
        value: customerTagsPayload,
      });
    }

    filteredDeepCopy = filteredDeepCopy.filter(
      (f) =>
        f.id === "customerTags" ||
        !(f.value instanceof Array) ||
        (f.value instanceof Array && !isCollectionNullOrEmpty(f.value))
    );
  }

  return filteredDeepCopy;
};

export const sortedModification = (sorted) => {
  const sortedCopy = _.cloneDeep(sorted);
  if (sortedCopy && sortedCopy.length > 0) {
    const idx = sortedCopy.findIndex((f) => f.id === "recentlyViewedUsers");
    if (idx > -1) sortedCopy[idx].id = "lastViewedBy";
  }
  return sortedCopy;
};

